/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import type { ProcessGuide } from '@/types/process-guides.types';
import ProcessFlowchart from './ProcessFlowchart';

interface ProcessGuideViewProps {
  guide: ProcessGuide;
  onBack?: () => void;
}

const ProcessGuideView: React.FC<ProcessGuideViewProps> = ({ guide, onBack }) => {
  const { t } = useTranslation('platform');
  const navigate = useNavigate();
  const [activeStepId, setActiveStepId] = useState<string | null>(null);
  const [collapsedSteps, setCollapsedSteps] = useState<Set<string>>(new Set());
  const stepRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const guideTitle = t(`guides.${guide.code}.title`, guide.code);
  const guideDescription = t(`guides.${guide.code}.description`, '');

  const handleStepClick = useCallback((stepId: string) => {
    setActiveStepId(stepId);
    const el = stepRefs.current.get(stepId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const toggleStep = useCallback((stepId: string) => {
    setCollapsedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) {
        next.delete(stepId);
      } else {
        next.add(stepId);
      }
      return next;
    });
  }, []);

  const setStepRef = useCallback((stepId: string, el: HTMLDivElement | null) => {
    if (el) {
      stepRefs.current.set(stepId, el);
    } else {
      stepRefs.current.delete(stepId);
    }
  }, []);

  const steps = useMemo(() => guide.steps, [guide.steps]);

  return (
    <div className="guide-view">
      {/* Header */}
      <button className="guide-view-back" onClick={onBack} type="button">
        <ChevronLeft size={18} />
        <span>{guideTitle}</span>
      </button>

      {guideDescription && (
        <p className="guide-view-description">{guideDescription}</p>
      )}

      {/* Flowchart */}
      <div className="guide-view-flowchart">
        <ProcessFlowchart
          guide={guide}
          activeStepId={activeStepId}
          onStepClick={handleStepClick}
        />
      </div>

      <hr className="guide-view-divider" />

      {/* Step list (timeline accordion) */}
      <div className="guide-view-steps">
        {steps.map((step, index) => {
          const isActive = activeStepId === step.id;
          const isCollapsed = collapsedSteps.has(step.id);
          const isLast = index === steps.length - 1;
          const stepTitle = t(`guides.${guide.code}.steps.${step.id}.title`, step.id);
          const stepDesc = t(`guides.${guide.code}.steps.${step.id}.description`, '');

          return (
            <div
              key={step.id}
              ref={(el) => setStepRef(step.id, el)}
              className={`guide-view-step${isActive ? ' guide-view-step--active' : ''}`}
            >
              {/* Timeline connector */}
              <div className="guide-view-timeline">
                <div className="guide-view-step-number">{index + 1}</div>
                {!isLast && <div className="guide-view-timeline-line" />}
              </div>

              {/* Step content */}
              <div className="guide-view-step-content">
                <button
                  className="guide-view-step-header"
                  onClick={() => toggleStep(step.id)}
                  type="button"
                >
                  <span className="guide-view-step-title">{stepTitle}</span>
                  {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                </button>

                {!isCollapsed && (
                  <div className="guide-view-step-body">
                    {stepDesc && <p className="guide-view-step-desc">{stepDesc}</p>}
                    {step.targetPath && (
                      <button
                        className="guide-view-goto"
                        onClick={() => navigate(step.targetPath!)}
                        type="button"
                      >
                        <ExternalLink size={14} />
                        <span>{t('guides.goToPage')}</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        .guide-view {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .guide-view-back {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          background: none;
          border: none;
          color: var(--color-text-primary);
          font-size: 1rem;
          font-weight: 600;
          font-family: var(--font-heading);
          cursor: pointer;
          padding: 0.25rem 0;
        }
        .guide-view-back:hover {
          color: var(--color-brand-primary);
        }
        .guide-view-description {
          color: var(--color-text-secondary);
          font-size: 0.875rem;
          line-height: 1.5;
          margin: 0;
        }
        .guide-view-flowchart {
          background: var(--color-bg-tertiary);
          border: 1px solid var(--color-border-subtle);
          border-radius: var(--radius-md);
          padding: 0.75rem;
        }
        .guide-view-divider {
          border: none;
          border-top: 1px solid var(--color-border-subtle);
          margin: 0.25rem 0;
        }

        /* Timeline steps */
        .guide-view-steps {
          display: flex;
          flex-direction: column;
        }
        .guide-view-step {
          display: flex;
          gap: 0.75rem;
          position: relative;
        }
        .guide-view-step--active .guide-view-step-content {
          border-color: var(--color-brand-primary);
        }
        .guide-view-timeline {
          display: flex;
          flex-direction: column;
          align-items: center;
          flex-shrink: 0;
          width: 28px;
        }
        .guide-view-step-number {
          width: 24px;
          height: 24px;
          border-radius: var(--radius-full);
          background: var(--color-brand-primary);
          color: #fff;
          font-size: 0.75rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .guide-view-timeline-line {
          width: 2px;
          flex: 1;
          background: var(--color-border-subtle);
          min-height: 16px;
        }

        /* Step content card */
        .guide-view-step-content {
          flex: 1;
          margin-bottom: 0.5rem;
          border: 1px solid transparent;
          border-radius: var(--radius-md);
          transition: border-color 0.2s ease;
        }
        .guide-view-step-content:hover {
          background: var(--color-bg-secondary);
        }
        .guide-view-step-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          background: none;
          border: none;
          padding: 0.375rem 0.5rem;
          cursor: pointer;
          color: var(--color-text-primary);
        }
        .guide-view-step-title {
          font-size: 0.875rem;
          font-weight: 600;
        }
        .guide-view-step-header svg {
          color: var(--color-text-muted);
          flex-shrink: 0;
        }
        .guide-view-step-body {
          padding: 0 0.5rem 0.5rem;
        }
        .guide-view-step-desc {
          color: var(--color-text-secondary);
          font-size: 0.8125rem;
          line-height: 1.5;
          margin: 0 0 0.5rem;
        }
        .guide-view-goto {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          background: none;
          border: none;
          color: var(--color-brand-primary);
          font-size: 0.8125rem;
          font-weight: 500;
          cursor: pointer;
          padding: 0.25rem 0;
        }
        .guide-view-goto:hover {
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default ProcessGuideView;
